/**
 * AGENT INSTALLER PROGRESS
 * 
 * Component responsável pelas barras de progresso do wizard de instalação
 */

import React, { useMemo } from 'react';
import { cn } from '@/infrastructure/lib/utils';
import { Check } from 'lucide-react';
import { InstallerStep, InstallationProgress } from '../../types/AgentInstaller';
import { useTranslation } from 'react-i18next';
import { translateDeep } from '@/shared/utils/i18nHelper';

interface AgentInstallerProgressProps {
  currentStep: number;
  totalSteps: number;
  steps: InstallerStep[];
  installationProgress: InstallationProgress;
  isInstalling: boolean;
  installationComplete: boolean;
}

export const AgentInstallerProgress: React.FC<AgentInstallerProgressProps> = ({
  currentStep,
  totalSteps,
  steps,
  installationProgress,
  isInstalling,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt' ou 'en'

  // Traduzir todos os steps
  const translatedSteps = useMemo(() => {
    return steps.map(step => translateDeep(step, currentLang));
  }, [steps, currentLang]);

  const currentStepData = translatedSteps[currentStep];

  return (
    <>
      {/* Progress Indicator Superior */}
      <div className="px-6 py-4 border-b bg-background">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            {t('installation.stepIndicator', { current: currentStep + 1, total: totalSteps })}
          </span>
          <span className="text-sm text-muted-foreground">
            {currentStepData?.title}
          </span>
        </div>
        <div className="flex gap-1">
          {translatedSteps.map((_step, index) => (
            <div
              key={index}
              className={cn(
                "h-2 rounded-full flex-1",
                index <= currentStep 
                  ? "bg-primary" 
                  : "bg-muted"
              )}
            />
          ))}
        </div>
      </div>

      {/* Progress Bar de Instalação (quando aplicável) */}
      {isInstalling && (
        <div className="px-6 py-4 border-b bg-muted/20">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">
                {installationProgress.currentStep}
              </span>
              <span className="text-muted-foreground">
                {installationProgress.percentage}%
              </span>
            </div>
            
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${installationProgress.percentage}%` }}
              />
            </div>
          </div>

          {/* Steps Detalhados de Instalação */}
          <div className="space-y-3 mt-6">
            {[
              { step: 'copying', label: t('installation.installationProcess.copying'), icon: '📋' },
              { step: 'integrations', label: t('installation.installationProcess.integrations'), icon: '🔌' },
              { step: 'parameters', label: t('installation.installationProcess.parameters'), icon: '⚙️' },
              { step: 'publishing', label: t('installation.installationProcess.publishing'), icon: '🚀' },
              { step: 'exporting', label: t('installation.installationProcess.exporting'), icon: '📤' },
              { step: 'finalizing', label: t('installation.installationProcess.finalizing'), icon: '✨' }
            ].map((item, index) => {
              const currentStepIndex = installationProgress.currentStepIndex || 0;
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const isPending = index > currentStepIndex;
              
              return (
                <div key={item.step} className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-all duration-300",
                  isCurrent && "bg-primary/5 border border-primary/20",
                  isCompleted && "bg-green-50 border border-green-200",
                  isPending && "bg-muted/30"
                )}>
                  <div className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-300",
                    isCurrent && "bg-primary text-primary-foreground animate-pulse",
                    isCompleted && "bg-green-500 text-white",
                    isPending && "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted ? '✓' : isCurrent ? (
                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : item.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm font-medium transition-colors duration-300",
                      isCurrent && "text-primary",
                      isCompleted && "text-green-700",
                      isPending && "text-muted-foreground"
                    )}>
                      {item.label}
                    </p>
                  </div>
                  
                  {isCompleted && (
                    <div className="text-green-500 animate-in slide-in-from-right-2 duration-300">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
